<template>
  <main class="patient-page">
    <div class="patient-page__body" ref="body">
      <div class="patient-page__row">
        <h1 class="patient-page__title">Пациенты</h1>
        <div class="patient-page__buttons">
          <button
            class="patient-page__buttons-export"
            type="button"
            v-if="hasDownloadPatient"
            v-bind:disabled="isAwaitDownloadRequest"
            v-on:click="onShowDownloadDialog">
            Скачать базу пациентов
          </button>
          <button
            class="patient-page__buttons-add"
            type="button"
            v-if="hasAddPatient"
            v-on:click="createPatient">
            Новый пациент
          </button>
        </div>
      </div>
      <div class="patient-page__filters">
        <input-text-search
          class="patient-page__search"
          v-bind:value="searchQuery"
          v-on:input="onInputSearchQuery"
          placeholder="Введите ФИО, телефон (без 8) или № медкарты"
        />
        <button
          class="patient-page__buttons-filter"
          type="button"
          v-on:click="onShowFilter"
          v-bind:class="{ active: isShowFilter }"
          v-bind:title="titleButtonFilters"
        >Фильтр</button>
        <popover-menu>
          <template v-slot:trigger="{ listenerObject }">
            <button
              v-on="listenerObject"
              class="patient-page__buttons-settings"
              type="button"
            />
          </template>
          <form-table-settings
            v-bind:columns="tableColumnSettings"
            v-on:change="onChangeColumnSettings($options.tableId, $event)"
          />
        </popover-menu>
      </div>
      <div
        v-if="isShowFilter"
        class="patient-page__filters-additional"
      >
        <div class="patient-page__date-range-wrapper">
          <label
            for="date-range"
            class="patient-page__filters-label"
          >Дата рождения</label>
          <day-month-range
            id="date-range"
            v-model="birthDateRange"
            v-on:input="onInputSearchDate" />
        </div>
        <button
          class="patient-page__buttons-reset"
          type="button"
          v-on:click="onCleanSearchDate">
          <img src="@/assets/img/cross-blue.svg" alt="">
          Сбросить
        </button>
      </div>
      <patient-table
        v-bind:column-settings="tableColumnSettings"
        v-bind:columns="tableColumns"
        v-bind:has-download="hasDownloadPatient"
        v-bind:has-copy="hasCopyPatient"
        v-bind:has-delete="hasDeletePatient"
        v-bind:has-view-cda-completeness="hasViewCdaCompleteness"
        v-bind:patient-list="filteredPatients"
        v-bind:is-download-disabled="isAwaitDownloadRequest"
        v-bind:is-await-request="isAwaitRequest"
        v-bind:sort="sort"
        v-on:change-column-settings="onChangeColumnSettings($options.tableId, $event)"
        v-on:download-patient="onDownloadPatient"
        v-on:copy-patient="onCopyPatient"
        v-on:delete-patient="onDeletePatient"
        v-on:edit-patient="editPatient"
        v-on:sort-patient-list="fetchSortedPatientList"
      />
      <the-pagination v-model="page" v-bind:page-count="pageCount" />
      <modal-dialog
        v-bind:with-cross="true"
        v-bind:value="shownPatientDialogDelete"
        v-on:input="closeDialogRemove"
        title="Удаление пациента"
      >
        <span class="v-card__text__description">При удалении пациента восстановить данные невозможно!</span>
        <ul
          class="v-card__cautions-list"
          v-for="caution in cautionListBeforeDelete"
          :key="caution"
        >
          <li>{{caution}}</li>
        </ul>
        <input-checkbox
          title='Ознакомлен и согласен с условиями удаления'
          v-model="acceptToDeletePatient"
        />
        <button
          class="patient-page__buttons-delete"
          type="button"
          v-bind:disabled="!acceptToDeletePatient"
          v-on:click="deletePatient"
        >Удалить</button>
      </modal-dialog>
    </div>
  </main>
</template>
<script>
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import customizableTableMixin from '@/mixins/customizableTableMixin';
import InputTextSearch from '@/components/InputTextSearch.vue';
import PatientTable from '@/components/PatientTable.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { APPOINTMENT_COPY } from '@/store/actions/appointments';
import ThePagination from '@/components/ThePagination.vue';
import debounce from 'lodash.debounce';
import ModalDialog from '@/components/ModalDialog.vue';
import InputCheckbox from '@/components/InputCheckbox.vue';
import DayMonthRange from '@/components/DayMonthRange.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import FormTableSettings from '@/components/FormTableSettings.vue';

/**
 * @description Идентификатор таблицы.
 * @type {string}
 */
const TABLE_ID = 'patients';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 * @returns {void}
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * @description Страница пациентов.
 */
export default {
  components: {
    DayMonthRange,
    InputCheckbox,
    ModalDialog,
    ThePagination,
    PatientTable,
    InputTextSearch,
    PopoverMenu,
    FormTableSettings,
  },
  tableId: TABLE_ID,
  async created() {
    this.fetchTableSettings(TABLE_ID);
    this.fetchSortedPatientList({ type: 'nearappointment', order: 'asc' }, this.initialSelectedPatientId);
  },
  computed: {
    /**
     * @description Может добавлять пациентов.
     * @type {boolean}
     */
    hasAddPatient() {
      return this.actions.includes('add-patient');
    },
    /**
     * @description Может копировать пациентов.
     * @type {boolean}
     */
    hasCopyPatient() {
      return this.actions.includes('copy-patient');
    },
    /**
     * @description Может удалять пациентов.
     * @type {boolean}
     */
    hasDeletePatient() {
      return this.actions.includes('delete-patient');
    },
    /**
     * @description Может выгружать пациентов.
     * @type {boolean}
     */
    hasDownloadPatient() {
      return this.actions.includes('download-patient');
    },
    /**
     * @description Можеть смотреть заполненность для СЭМД.
     */
    hasViewCdaCompleteness() {
      return this.actions.includes('view-cda-completeness-patient');
    },
    /**
     * @description Отфильтрованный список пациентов.
     * @type {any[]}
     */
    filteredPatients() {
      return this.selectedPatientId
        ? this.patientList.filter((candidate) => candidate.id === this.selectedPatientId)
        : this.patientList;
    },
    /**
     * @description Заголовок кнопки фильтров.
     * @returns {string}
     */
    titleButtonFilters() {
      return this.isShowFilter ? 'Скрыть фильтры' : 'Показать фильтры';
    },
  },
  data() {
    return {
      /**
       * @description Допустимые действия.
       * @type {string[]}
       */
      actions: [],
      /**
       * @description Фильтр даты рождения.
       * @type {(string[])}
       */
      birthDateRange: [],
      /**
       * @description Список пациентов.
       * @type {any[]}
       */
      patientList: [],
      /**
       * @description Поисковой запрос.
       * @type {string}
       */
      searchQuery: '',
      selectedPatientId: null,
      /**
       * @description Ожидание завершения запроса.
       * @type {boolean}
       */
      isAwaitRequest: false,
      /**
       * @description Ожидание завершения запроса на выгрузку.
       * @type {boolean}
       */
      isAwaitDownloadRequest: false,
      /**
       * @description Номер страницы.
       * @type {number}
       */
      page: 1,
      /**
       * @description Количество страниц.
       * @type {number}
       */
      pageCount: 0,
      /**
       * @description Тип сортировки
       * @type {Object}
       */
      sort: { type: '', order: '' },
      /**
       * @description Отображать диалоговое окно удаления пациента.
       * @type {Boolean}
       */
      shownPatientDialogDelete: false,
      /**
       * Список предупреждений
       * @type {Array<string>}
       */
      cautionListBeforeDelete: [],
      /**
       * @description Подтвердить удаление пациента.
       * @type {Boolean}
       */
      acceptToDeletePatient: false,
      /**
       * @description Идентификатор пациента на удаление.
       */
      patientIdToDelete: null,
      /**
       * @description Показывать ли фильтры
       */
      isShowFilter: false,
    };
  },
  methods: {
    /**
     * @description Открыть страницу создания пациента.
     * @returns {void}
     */
    async createPatient() {
      await this.$router.push({ name: 'ClinicPanelFormCreatePatient' });
    },
    /**
     * @description Открыть страницу редактирования пациента.
     * @param {number} patientId Идентификатор пациента.
     * @returns {void}
     */
    async editPatient(patientId) {
      const patient = this.patientList.find((p) => p.id === patientId);
      await this.$router.push({ name: 'ClinicPanelPatientBasic', params: { id: patient.id } });
    },
    /**
     * Получить сортированный список пациентов по типу
     * @param {Object}  sort      Тип сортировки.
     * @param {?number} patientId Идентификатор пациента.
     * @returns {Promise<void>}
     */
    async fetchSortedPatientList(sort, patientId = null) {
      this.sort = sort;
      const onSuccess = ({
        actions,
        patients,
        pageCount,
        columns,
      }) => {
        this.actions = actions;
        this.patientList = patients;
        this.pageCount = pageCount;
        this.tableColumns = columns;
      };
      this.isAwaitRequest = true;
      const either = await this.$patientGateway.getAll(
        this.sort,
        this.page,
        this.searchQuery,
        this.birthDateRange.length !== 2
          ? {}
          : {
            from: this.birthDateRange[0],
            to: this.birthDateRange[1],
          },
      );
      either.fold(this.notifyFail, onSuccess);
      this.isAwaitRequest = false;
      this.selectedPatientId = patientId;
    },
    /**
     * @description Обработать событие удаления пациента.
     * @param {number} id Идентификатор пациента.
     * @returns {void}
     */
    async onDeletePatient(id) {
      this.patientIdToDelete = id;
      const either = await this.$patientGateway.checkPatientCanBeRemoved(this.patientIdToDelete);
      either.fold(
        (error) => this.notifyFail(error.title),
        (response) => {
          this.cautionListBeforeDelete = response.data.list_cautions;
          this.shownPatientDialogDelete = true;
        },
      );
    },
    /**
     * @description Удалить пациента.
     * @returns {void}
     */
    async deletePatient() {
      const either = await this.$patientGateway.sendDeletePatient(this.patientIdToDelete);
      either.fold(
        (error) => this.notifyFail(error.title),
        (response) => {
          this.notifySuccess(response.message);
          this.patientList = this
            .patientList
            .filter((patient) => patient.id !== this.patientIdToDelete);
          this.closeDialogRemove();
        },
      );
    },
    /**
     * @description Закрыть диалоговое окно удаления.
     */
    closeDialogRemove() {
      this.shownPatientDialogDelete = false;
      this.patientIdToDelete = null;
    },
    /**
     * @description Выгрузить в Excel.
     * @returns {void}
     */
    exportToExcel() {
      const onFail = (error) => this.notifyFail(error.title);
      const onSuccess = (file) => saveFile(file, 'Список пациентов.xlsx');
      this.isAwaitRequest = true;
      this
        .$patientGateway
        .getAllBySheet(this.viewQuery)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    /**
     * @description Обработать копирование пациента.
     * @param {number} id Идентификатор пациента.
     * @returns {void}
     */
    onCopyPatient(id) {
      const appointmentCopy = {
        patientId: id,
        source: 'patient',
      };
      this.$store.dispatch(APPOINTMENT_COPY, appointmentCopy);
      this.selectedSlot = null;
      this.notifySuccessLong(
        'Пациент скопирован.'
        + ' Нажмите правой кнопкой мыши на нужный слот времени в расписании и вставьте запись.',
      );
    },
    /**
     * @description Обработать выгрузку пациента.
     * @param {number} patientId
     * @returns {void}
     */
    onDownloadPatient(patientId) {
      const patient = this.patientList.find((candidate) => candidate.id === patientId);
      if (!patient) {
        return;
      }
      const initials = patient
        .fullName
        .split(/\s/g)
        .slice(1)
        .filter((candidate) => !!candidate)
        .map((name) => name.charAt(0))
        .join('. ');
      const fileName = `${patient.lastName} ${initials}.zip`;
      this.isAwaitDownloadRequest = true;
      const onSuccess = (response) => saveFile(response, fileName);
      this
        .$patientGateway
        .download(patientId)
        .then((result) => result.fold(this.notifyFail, onSuccess))
        .finally(() => {
          this.isAwaitDownloadRequest = false;
        });
    },
    /**
     * @description Показать диалог выгрузки пациентов.
     * @returns {Promise<void>}
     */
    async onShowDownloadDialog() {
      const dialog = await this.showDialog(
        `Подготовка архива занимает время.
        Не вносите изменения в этом разделе до окончания загрузки.
        Начать скачивание?`,
        DialogResult.YES,
        DialogResult.NO,
      );
      if (dialog.result === DialogResult.YES) {
        this.onDownloadPatientList();
      }
    },
    /**
     * @description Ожидание готовности выгрузки.
     * @param {string} downloadId Идентификатор выгрузки.
     * @returns {Promise}
     */
    awaitDownloadPatientList(downloadId) {
      const execute = (resolve, reject, retry) => {
        const onFail = (error) => {
          if (error.has('waiting')) {
            retry();
          } else {
            reject(error.getTitle());
          }
        };
        this
          .$patientGateway
          .downloadListById(downloadId)
          .then((result) => result.fold(onFail, resolve))
          .catch(() => {
            this.isAwaitDownloadRequest = false;
          });
      };
      return new Promise((resolve, reject) => {
        const retry = () => setTimeout(() => execute(resolve, reject, retry), 7_000);
        retry();
      });
    },
    /**
     * @description Обработать скачивание выгрузки пациентов.
     * @returns {void}
     */
    onDownloadPatientList() {
      this.isAwaitDownloadRequest = true;
      const onDownload = (response) => saveFile(response, 'Пациенты.zip');
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this
          .awaitDownloadPatientList(response.downloadId)
          .then(onDownload, this.notifyFail);
      };
      this
        .$patientGateway
        .downloadList()
        .then((result) => result.fold(this.notifyFail, onSuccess));
    },
    /**
     * @description Обработать ввод запроса.
     * @type {string} value
     * @returns {void}
     */
    onInputSearchQuery(value) {
      this.searchQuery = value;
      this.selectedPatientId = null;
    },
    /**
     * @description Обработать ввод запроса по фильтру "По дате рождения".
     * @returns {void}
     */
    onInputSearchDate() {
      this.selectedPatientId = null;
    },
    /**
     * Очистить параметры фильтра "По дате рождения"
     *
     * @returns {void}
     */
    onCleanSearchDate() {
      this.birthDateRange = [];
    },
    /**
     * Показать или скрыть фильтры
     *
     * @returns {void}
     */
    onShowFilter() {
      this.isShowFilter = !this.isShowFilter;
    },
    /**
     * @description Прокрутить до начала страницы.
     * @returns {void}
     */
    scrollToTop() {
      this.$refs.body.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
  },
  mixins: [
    builtInDialogMixin,
    customizableTableMixin,
    notifierMixin,
  ],
  name: 'ClinicPanelPatientList',
  watch: {
    birthDateRange: {
      handler: debounce(async function _() {
        this.page = 1;
        await this.fetchSortedPatientList(this.sort);
      }, 300),
    },
    page: {
      handler: debounce(function _() {
        this.fetchSortedPatientList(this.sort);
        this.scrollToTop();
      }),
    },
    searchQuery: {
      handler: debounce(async function _() {
        this.page = 1;
        await this.fetchSortedPatientList(this.sort);
      }, 300),
    },
  },
  props: {
    initialSelectedPatientId: {
      default: () => null,
      type: Number,
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.v-snack__content {
  white-space: pre-line;
}

.v-card__title {
  margin-bottom: 10px;
}

.v-card__cautions-list {
  color: red;
}
.v-card__text {
  display: grid;
  gap: 15px;
  &__description {
    font-size: 16px;
  }
}

.patient {
  &-page {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    overflow: auto;
    &__filters {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 20px;
      &-label {
        color: gray;
        font-size: 12px;
      }
      &-additional {
        display: flex;
        gap:10px;
        margin-top: 20px;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1.2fr 1fr;
      max-width: 425px;
      width: 100%;
      column-gap: 10px;
      align-items: center;
      &-filter {
        margin-top: 25px;
        display: flex;
        align-items: center;
        column-gap: 4px;
        max-width: 165px;
        height: 40px;
        font-size: 16px;
        line-height: 20px;
        color: #0489e0;
        &::before {
          content: '';
          flex-shrink: 0;
          width: 16px;
          height: 16px;
          background: url("@/assets/img/filter.svg") no-repeat center;
          background-size: 16px;
        }
        &::after {
          content: "";
          background-image: url("@/assets/img/checked/checked-grey.svg");
          background-size: contain;
          background-repeat: no-repeat;
          width: 14px;
          height: 8px;
          margin: 0 0 0 3px;
        }
        &.active::after {
          transform: rotate(180deg);
        }
      }
      &-reset {
        margin-top: 25px;
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 175px;
        width: 100%;
        color: #1F85C8;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        >img {
          width: 15px;
          height: 15px;
        }
      }
      &-settings {
        display: none;
        margin-top: 25px;
        background-image: url("@/assets/img/settings-blue.svg");
        background-size: contain;
        background-repeat: no-repeat;
        width: 16px;
        height: 16px;
        min-width: 16px;
      }
      &-add {
        grid-column: 2/3;
        @include buttons.btn-blue(100%, 48px, 16px);
        @include buttons.btn-blue-add(19px);
      }

      &-export {
        @include buttons.btn-transparent(100%, 48px, 16px);

        &:before {
          content: "";
          background-image: url("@/assets/img/download-file-blue.svg");
          background-repeat: no-repeat;
          background-size: contain;
          width: 20px;
          height: 20px;
          margin: 0 8px 0 0;
        }

        &:disabled {
          color: var(--unfocus-color);
          border-color: var(--unfocus-color);
          background-color: transparent;
        }
      }

      &-delete {
        @include buttons.btn-blue(50%, 48px, 16px);
        width: 100%;
      }

      &-delete:disabled {
          background-color: var(--unfocus-color);
      }
    }

    &__body {
      padding: 38px 20px 48px 23px;
      width: 100%;
      overflow-y: scroll;
    }

    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }

    &__search {
      max-width: 400px;
    }
  }
}

@media screen and (max-width: 1250px) {
  .patient-page__buttons-settings {
    display: block;
  }
}

@media screen and (max-width: 960px) {
  .patient {
    &-page {
      flex-direction: column;

      &__row {
        flex-direction: column;
        gap: 10px;
      }

      &__search {
        max-width: 100%;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .patient {
    &-page {
      &__body {
        padding: 38px 10px 140px;
        width: 100%;
        overflow-y: scroll;
      }

      &__buttons {
        grid-template-columns: 2fr 1fr;
        max-width: 100%;
        row-gap: 10px;
      }
    }
  }
}

@media screen and (max-width: 600px) {
  .patient-page {
    &__date-range-wrapper {
      width: min(100%, 15.625rem);
    }
  }
}

@media screen and (max-width: 480px) {
  .v-text-field__slot input::placeholder {
    font-size: 12px;
  }
  .patient-page {
    &__buttons-export {
      display: none;
    }
    &__buttons {
      grid-template-columns: 1fr 1fr;
      &-settings {
        display: none;
      }
    }
  }
}

@media screen and (max-width: 400px) {
  .patient-page {
    &__date-range-wrapper {
      .day-month-range {
        width: 12rem;
      }
    }
  }
}
</style>
